import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Sparkles, ArrowRight } from 'lucide-react';
import { useAuthContext } from '../contexts/AuthContext';

interface CrossProductUpgradeCardProps {
  type: 'teacher' | 'salepage';
}

const CrossProductUpgradeCard: React.FC<CrossProductUpgradeCardProps> = ({ type }) => {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  // Don't show if user already has both
  if (user?.userType === 'both') {
    return null;
  }

  // Don't show teacher card if user is already a teacher
  if (type === 'teacher' && user?.userType === 'teacher') {
    return null;
  }

  // Don't show salepage card if user is already a salepage user
  if (type === 'salepage' && user?.userType === 'salepage') {
    return null;
  }

  if (type === 'teacher') {
    return (
      <div className="bg-gradient-to-br from-blue-500 to-fuchsia-600 rounded-2xl shadow-xl p-8 text-white">
        <div className="flex items-start gap-6">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-2xl font-bold mb-2">
              ต้องการสร้างคอร์สเรียนด้วย?
            </h3>
            <p className="text-white/90 mb-6">
              อัพเกรดเป็นผู้สอนเพื่อสร้างคอร์สออนไลน์ รับนักเรียนไม่จำกัด และจัดการเนื้อหาได้อย่างเต็มรูปแบบ
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs">✓</span>
                </div>
                <span className="text-sm">สร้างคอร์สเรียนไม่จำกัด</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs">✓</span>
                </div>
                <span className="text-sm">รับนักเรียนไม่จำกัด</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs">✓</span>
                </div>
                <span className="text-sm">พื้นที่จัดเก็บ 20-35 GB</span>
              </li>
            </ul>
            <button
              onClick={() => navigate('/pricing')}
              className="px-6 py-3 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all inline-flex items-center gap-2"
            >
              ดูแพ็คเกจผู้สอน
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Salepage card
  return (
    <div className="bg-gradient-to-br from-emerald-500 to-cyan-600 rounded-2xl shadow-xl p-8 text-white">
      <div className="flex items-start gap-6">
        <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-2xl font-bold mb-2">
            ต้องการสร้างหน้าขายสินค้า?
          </h3>
          <p className="text-white/90 mb-6">
            เพิ่ม Salepage Builder เพื่อสร้างหน้าขายสินค้าสวยๆ ใน 5 นาที พร้อมแชร์ได้ทันที
          </p>
          <ul className="space-y-2 mb-6">
            <li className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs">✓</span>
              </div>
              <span className="text-sm">สร้างหน้าขายได้ 1 หน้า</span>
            </li>
            <li className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs">✓</span>
              </div>
              <span className="text-sm">วิดีโอ + รูปภาพ + ข้อความ</span>
            </li>
            <li className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs">✓</span>
              </div>
              <span className="text-sm">เริ่มต้นเพียง 149฿/เดือน</span>
            </li>
          </ul>
          <button
            onClick={() => window.open('/salepage/pricing', '_blank')}
            className="px-6 py-3 bg-white text-emerald-600 rounded-xl font-semibold hover:bg-emerald-50 transition-all inline-flex items-center gap-2"
          >
            ดูแพ็คเกจ Salepage
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CrossProductUpgradeCard;
